import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';

export interface HoverCardProps {
  children: React.ReactNode;
  content: React.ReactNode;
  enterDelay?: number;
  leaveDelay?: number;
  disabled?: boolean;
  className?: string;
  // Override the card's max width — useful for hovercards carrying
  // richer content (DAG node details, prompt/response previews).
  maxWidth?: number;
}

const DEFAULT_MAX_WIDTH = 280;
const VIEWPORT_MARGIN = 8;
const ARROW_SIZE = 6;
const GAP = 8;

interface Placement { top: number; left: number; above: boolean; arrowLeft: number }

// Coordinates returned here are VIEWPORT-relative (used with
// position:fixed downstream). DOMRect.top/left already are viewport
// coordinates — we deliberately do NOT add window.scrollY/scrollX,
// which would push the card past document bounds and grow body's
// scroll height when the card lands near a viewport edge. That
// growth was causing a page scrollbar to appear on hover, which
// reflowed the ChatMap container narrower → visible "jiggle."
function computePlacement(rect: DOMRect, cardH: number, cardW: number): Placement {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const above = rect.top - VIEWPORT_MARGIN >= cardH + GAP || rect.top > vh - rect.bottom;
  const rawTop = above ? rect.top - cardH - GAP : rect.top + rect.height + GAP;
  // Clamp into viewport so the card never extends past top/bottom edges.
  const top = Math.max(VIEWPORT_MARGIN, Math.min(rawTop, vh - cardH - VIEWPORT_MARGIN));

  const idealLeft = rect.left + rect.width / 2 - cardW / 2;
  const left = Math.max(VIEWPORT_MARGIN, Math.min(idealLeft, vw - cardW - VIEWPORT_MARGIN));
  const arrowLeft = Math.max(ARROW_SIZE * 2, Math.min(rect.left + rect.width / 2 - left, cardW - ARROW_SIZE * 2));

  return { top, left, above, arrowLeft };
}

const HoverCard: React.FC<HoverCardProps> = ({
  children, content, enterDelay = 300, leaveDelay = 120, disabled = false, className = '',
  maxWidth = DEFAULT_MAX_WIDTH,
}) => {
  const { darkMode } = useTheme();
  const uid = useId();
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState<Placement | null>(null);

  const trigHover = useRef(false);
  const cardHover = useRef(false);
  const enterT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const leaveT = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trigRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const clear = useCallback(() => {
    if (enterT.current) { clearTimeout(enterT.current); enterT.current = null; }
    if (leaveT.current) { clearTimeout(leaveT.current); leaveT.current = null; }
  }, []);

  const position = useCallback(() => {
    if (!trigRef.current) return;
    // display:contents means the span has no box — measure first child element instead
    const el = trigRef.current.firstElementChild as HTMLElement | null;
    const rect = (el || trigRef.current).getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) return; // nothing to anchor to
    setPos(computePlacement(rect, 180, maxWidth));
    requestAnimationFrame(() => {
      if (cardRef.current && trigRef.current) {
        const h = cardRef.current.offsetHeight;
        if (Math.abs(h - 180) > 4) {
          const el2 = trigRef.current.firstElementChild as HTMLElement | null;
          const rect2 = (el2 || trigRef.current).getBoundingClientRect();
          setPos(computePlacement(rect2, h, maxWidth));
        }
      }
    });
  }, [maxWidth]);

  const show = useCallback(() => {
    if (disabled) return;
    clear();
    enterT.current = setTimeout(() => { position(); setVisible(true); }, enterDelay);
  }, [disabled, enterDelay, clear, position]);

  const hide = useCallback(() => {
    clear();
    leaveT.current = setTimeout(() => {
      if (!trigHover.current && !cardHover.current) { setVisible(false); setPos(null); }
    }, leaveDelay);
  }, [leaveDelay, clear]);

  useEffect(() => {
    if (!visible) return;
    const u = () => position();
    window.addEventListener('scroll', u, { passive: true, capture: true });
    window.addEventListener('resize', u, { passive: true });
    return () => { window.removeEventListener('scroll', u, true); window.removeEventListener('resize', u); };
  }, [visible, position]);

  useEffect(() => () => clear(), [clear]);

  if (disabled) return <>{children}</>;

  const card = visible && pos ? (
    <div
      ref={cardRef}
      id={`hc-${uid}`}
      role="tooltip"
      onMouseEnter={() => { cardHover.current = true; clear(); }}
      onMouseLeave={() => { cardHover.current = false; hide(); }}
      style={{ position: 'fixed', top: pos.top, left: pos.left, width: maxWidth, zIndex: 9999 }}
      className={`pointer-events-auto rounded-lg shadow-lg border backdrop-blur-sm animate-in fade-in-0 duration-200 ease-out ${
        darkMode ? 'bg-gray-900/95 border-gray-700 text-gray-200' : 'bg-white/95 border-gray-200 text-gray-800'
      }`}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute', left: pos.arrowLeft - ARROW_SIZE, width: 0, height: 0,
          ...(pos.above
            ? { bottom: -(ARROW_SIZE * 2), borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`, borderTop: `${ARROW_SIZE * 2}px solid ${darkMode ? 'rgb(55,65,81)' : 'rgb(229,231,235)'}` }
            : { top: -(ARROW_SIZE * 2), borderLeft: `${ARROW_SIZE}px solid transparent`, borderRight: `${ARROW_SIZE}px solid transparent`, borderBottom: `${ARROW_SIZE * 2}px solid ${darkMode ? 'rgb(55,65,81)' : 'rgb(229,231,235)'}` }),
        }}
      />
      <div className="p-3">{content}</div>
    </div>
  ) : null;

  return (
    <span
      ref={trigRef}
      className={className}
      style={{ display: 'contents' }}
      onMouseEnter={() => { trigHover.current = true; show(); }}
      onMouseLeave={() => { trigHover.current = false; hide(); }}
      aria-describedby={visible ? `hc-${uid}` : undefined}
    >
      {children}
      {card && createPortal(card, document.body)}
    </span>
  );
};

export default HoverCard;
